"use client";

import { ButtonThemeSwitcher, Image, View } from "@/components";

import type { JSX } from "react";

/**
 * @name Nav
 * @description The top navigation bar component. It always renders the theme switcher, and shows the
 * institutional logos while the user is not authenticated.
 *
 * DIVERGENCIA CONTRA EL TEMPLATE (revisar en el proximo sync con
 * template_frontend/develop): a pedido del cliente la barra es azul marino
 * institucional en los dos temas, con la linea inferior dorada. El template la
 * cablea a los tokens `--navbar` / `--navbar-border`, que en claro dan blanco +
 * dorado y en oscuro negro + azul electrico.
 *
 * Los colores van literales y no como `bg-(--sv-navy)`: el Nav lo monta el
 * LayoutProvider para TODAS las rutas, pero `app/brand/brand.css` —donde viven
 * los tokens de marca— solo se importa desde los layouts de la superficie del
 * alumno, asi que en /signIn y en /backoffice esas variables no existen y el
 * fondo quedaria transparente. Los valores espejan `--sv-navy` y `--sv-yellow`
 * de esa hoja: si cambian alla, cambian aca.
 *
 * Los logos ahora se muestran SIEMPRE. El template los reservaba para la rama
 * sin sesion y con `userAuth` renderizaba busqueda + campana + perfil + toggle
 * del sidebar; esos cuatro ya se habian sacado a pedido del cliente, asi que la
 * rama autenticada solo dejaba un spacer y la barra quedaba sin identidad
 * apenas entrabas. Sus mocks muestran los logos tambien con sesion iniciada.
 * Para reponer el bloque del template hay que volver a importar `SearchBar`,
 * `Notification`, `Profile` y `useLayout` (de ahi salia `userAuth`).
 *
 * Ojo con lo que se saco: el logout vivia en el menu de perfil, y hoy lo cubren
 * `SidebarVertical` (backoffice) y `StudentSessionBar` (alumno), pero NO
 * `SidebarHovered` ni `SidebarTwoColumn`. El toggle del sidebar ademas venia
 * roto de origen —pasaba su `action` dentro de `iconLeft`, que `Icon` descarta
 * al filtrar props—; el disparador vivo es el organismo `SidebarToggle`, que usa
 * el mismo `id="sidebar-toggle"` que buscaba este.
 *
 * @returns {JSX.Element} The rendered navigation bar.
 */
const Nav = (): JSX.Element => (
  <View
    type="nav"
    role="navigation"
    className="fixed top-0 left-0 right-0 px-4 xxl:px-6 shadow-sm z-[105] flex items-center justify-between min-h-[84px] bg-[#242c4f] border-b-[6px] border-[#e7ba61] text-white"
  >
    <View className="flex w-full items-center gap-2">
      <View className="flex w-full items-center justify-between">
        {/*
          Lockup institucional provisto por el cliente: trae los dos logos y su
          separacion en las proporciones del manual, en un unico SVG vectorial.
          Antes eran dos imagenes sueltas con un separador dibujado a mano, y el
          wordmark quedaba mas grande que el escudo respecto del manual.
          La version blanca es la que pide el fondo azul marino: va sin filtros
          (el template las pasaba por `brightness-0 opacity-80` para
          ennegrecerlas sobre la barra blanca, y eso era lo que el cliente marco
          como "Logos"). La variante azul del mismo lockup esta en la carpeta
          `Logos/` del workspace, para cuando haga falta sobre fondo claro.
        */}
        <View className="h-12 sm:h-16 flex items-center">
          <Image
            src="/img/logos-sv-st-blanco.svg"
            alt="Seguridad Vial - Secretaria de Transporte - Ministerio de Economia"
            width={1127}
            height={288}
            loading="eager"
            className="object-contain !w-auto !h-full"
          />
        </View>
      </View>
      <ButtonThemeSwitcher />
    </View>
  </View>
);

export default Nav;
